import {
  CIRCULAR_PLACEHOLDER,
  REDACTED_NATIONAL_ID_PLACEHOLDER,
  REDACTED_PLACEHOLDER,
  TRUNCATED_PLACEHOLDER,
} from './constants.js';
import { emittableError, isErrorLike, type ErrorLike } from './error-fields.js';

/**
 * Redaction of direct identifiers in everything the logger writes to stdout.
 * The Sentry `beforeSend` / `beforeBreadcrumb` hooks reuse this denylist, so
 * the two paths cannot drift apart.
 *
 * Only console output is redacted. Sinks receive the live entry (an `Error` on
 * the `error` key must stay an instance so `captureException` can group by
 * stack); minimizing what leaves the process is the sink's job.
 */

/**
 * Key fragments that mark a direct identifier wherever they appear, matched as
 * substrings so `patientPersonIdentifier` and `person_identifier` are caught
 * too. Only unambiguous fragments belong here.
 */
const SENSITIVE_KEY_FRAGMENTS = [
  'personidentifier',
  'personnummer',
  'fodselsnummer',
  'fodselsnr',
  'nationalid',
  'socialsecurity',
  'patientname',
  'hprnumber',
  'phonenumber',
  'emailaddress',
];

/**
 * Keys that are sensitive as a whole word only. `name` cannot be matched as a
 * substring without redacting `fileName`, `eventName`, `scopeName` and most of
 * what makes a log useful.
 */
const SENSITIVE_KEY_EXACT = new Set([
  'pid',
  'ssn',
  'hpr',
  'fnr',
  'name',
  'fullname',
  'displayname',
  'firstname',
  'lastname',
  'givenname',
  'familyname',
  'surname',
  'middlename',
  'email',
  'phone',
  'mobile',
  'address',
  'streetaddress',
  'dateofbirth',
  'birthdate',
  'dob',
]);

/**
 * A Norwegian fødselsnummer/D-number: 11 digits, optionally split after the
 * six-digit birth date (`010190 12345`). Word boundaries keep longer digit runs
 * (epoch millis, ID sequences) out of it.
 */
const NATIONAL_ID_PATTERN = /\b\d{6}[ -]?\d{5}\b/g;

/** Guards against pathological nesting when redacting arbitrary context. */
const MAX_DEPTH = 8;

/** Lowercase and drop separators so `person_identifier` matches `personIdentifier`. */
function normalizeKey(key: string): string {
  return key.toLowerCase().replaceAll(/[^a-z0-9]/g, '');
}

/**
 * Whether a log-context key holds a direct identifier and so must never reach a
 * sink. Shared with the Sentry scrubber so both paths agree on the denylist.
 */
export function isSensitiveLogKey(key: string): boolean {
  const normalized = normalizeKey(key);
  return (
    SENSITIVE_KEY_EXACT.has(normalized) ||
    SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment))
  );
}

/**
 * Mask every fødselsnummer-shaped run of digits in a string. Catches the value
 * that reached a *non*-sensitive key (a message, a URL, an `Error.message`),
 * which key-based redaction cannot see.
 */
export function maskNationalIds(value: string): string {
  return value.replaceAll(NATIONAL_ID_PATTERN, REDACTED_NATIONAL_ID_PLACEHOLDER);
}

/**
 * Render an error as a readable object. Errors carry no enumerable own
 * properties, so they would serialize to `{}`. What may be emitted is decided by
 * {@link emittableError}; this adds key redaction and national-ID masking.
 */
function redactError(
  error: ErrorLike,
  depth: number,
  seen: WeakSet<object>,
): Record<string, unknown> {
  const emittable = emittableError(error);
  const out: Record<string, unknown> = {};
  for (const [key, value] of emittable.fields) {
    out[key] = isSensitiveLogKey(key) ? REDACTED_PLACEHOLDER : redactValue(value, depth + 1, seen);
  }
  out.name = emittable.name;
  out.message = maskNationalIds(emittable.message);
  if (emittable.stack !== undefined) {
    out.stack = maskNationalIds(emittable.stack);
  }
  if (emittable.cause !== undefined) {
    out.cause = redactValue(emittable.cause, depth + 1, seen);
  }
  return out;
}

function redactValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return maskNationalIds(value);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (seen.has(value)) {
    return CIRCULAR_PLACEHOLDER;
  }
  if (depth >= MAX_DEPTH) {
    return TRUNCATED_PLACEHOLDER;
  }
  seen.add(value);
  try {
    if (isErrorLike(value)) {
      return redactError(value, depth, seen);
    }
    if (Array.isArray(value)) {
      return value.map((item) => redactValue(item, depth + 1, seen));
    }
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      out[key] = isSensitiveLogKey(key) ? REDACTED_PLACEHOLDER : redactValue(val, depth + 1, seen);
    }
    return out;
  } finally {
    seen.delete(value);
  }
}

/**
 * Deep-copy an arbitrary value with sensitive keys redacted, national
 * identifiers masked, and errors serialized. Cycle- and depth-safe, so the
 * result is always `JSON.stringify`-able. The input is never mutated.
 */
export function redactLogValue(value: unknown): unknown {
  return redactValue(value, 0, new WeakSet<object>());
}
